import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { FeatherArrowLeft, FeatherSave } from "@subframe/core";
import { useToast } from "../../../../shared/presentation/components";
import { getModulesAndActions, createRole } from "../../data/api/rolesApi";
import { RolePermissionMatrix } from "../components/RolePermissionMatrix";

interface Module {
  id: number;
  module_code: string;
  module_name: string;
  actions: Array<{
    id: number;
    action_code: string;
    action_name: string;
  }>;
}

export const AddRolePage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState({
    role_name: "",
    description: "",
    is_manager_required: false,
  });

  const [modules, setModules] = useState<Module[]>([]);
  const [permissions, setPermissions] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [reviewPolicies, setReviewPolicies] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingModules, setLoadingModules] = useState(true);

  // Fetch modules and actions on mount
  useEffect(() => {
    const loadModules = async () => {
      try {
        setLoadingModules(true);
        const response = await getModulesAndActions();
        if (response.success && response.data) {
          setModules(response.data);
        } else {
          showError("Failed to load modules and actions");
        }
      } catch (error: any) {
        showError(error.message || "Failed to load modules and actions");
      } finally {
        setLoadingModules(false);
      }
    };

    loadModules();
  }, [showError]);

  const handlePermissionChange = (
    moduleCode: string,
    actionCode: string,
    granted: boolean
  ) => {
    setPermissions((prev) => {
      const newPermissions = { ...prev };
      if (!newPermissions[moduleCode]) {
        newPermissions[moduleCode] = {};
      }
      if (granted) {
        newPermissions[moduleCode][actionCode] = true;
      } else {
        delete newPermissions[moduleCode][actionCode];
        if (Object.keys(newPermissions[moduleCode]).length === 0) {
          delete newPermissions[moduleCode];
        }
      }
      return newPermissions;
    });

    // If unchecking permission, also remove review policy
    if (!granted) {
      setReviewPolicies((prev) => {
        const newReviewPolicies = { ...prev };
        if (newReviewPolicies[moduleCode]) {
          delete newReviewPolicies[moduleCode][actionCode];
          if (Object.keys(newReviewPolicies[moduleCode]).length === 0) {
            delete newReviewPolicies[moduleCode];
          }
        }
        return newReviewPolicies;
      });
    }
  };

  const handleReviewPolicyChange = (
    moduleCode: string,
    actionCode: string,
    requiresReview: boolean
  ) => {
    setReviewPolicies((prev) => {
      const newReviewPolicies = { ...prev };
      if (!newReviewPolicies[moduleCode]) {
        newReviewPolicies[moduleCode] = {};
      }
      if (requiresReview) {
        newReviewPolicies[moduleCode][actionCode] = true;
      } else {
        delete newReviewPolicies[moduleCode][actionCode];
        if (Object.keys(newReviewPolicies[moduleCode]).length === 0) {
          delete newReviewPolicies[moduleCode];
        }
      }
      return newReviewPolicies;
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.role_name.trim()) {
      newErrors.role_name = "Role name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setIsLoading(true);

      // Log what we're sending for debugging
      console.log("Creating role with data:", {
        role_name: formData.role_name.trim(),
        is_manager_required: formData.is_manager_required ? 1 : 0,
        permissions_count: Object.keys(permissions).length,
        permissions,
        review_policies_count: Object.keys(reviewPolicies).length,
        review_policies: formData.is_manager_required ? reviewPolicies : {},
      });

      await createRole({
        role_name: formData.role_name.trim(),
        description: formData.description.trim() || undefined,
        is_manager_required: formData.is_manager_required ? 1 : 0,
        permissions,
        review_policies: formData.is_manager_required ? reviewPolicies : {},
      });

      showSuccess("Role created successfully");
      navigate("/admin/dashboard?tab=roles");
    } catch (error: any) {
      showError(error.message || "Failed to create role");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6 p-8">
        {/* Header */}
        <div className="flex w-full items-center gap-4">
          <Button
            variant="neutral-tertiary"
            icon={<FeatherArrowLeft />}
            onClick={() => navigate("/admin/dashboard?tab=roles")}
          >
            Back
          </Button>
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Create Role
            </span>
            <span className="text-body font-body text-subtext-color">
              Create a new role with permissions and review policies
            </span>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-col items-start gap-6"
        >
          {/* Basic Information */}
          <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Basic Information
            </span>

            <div className="flex w-full flex-col gap-4">
              <TextField
                className="h-auto w-full flex-none"
                label="Role Name *"
                helpText={errors.role_name || ""}
                error={!!errors.role_name}
              >
                <TextField.Input
                  name="role_name"
                  type="text"
                  placeholder="Enter role name"
                  value={formData.role_name}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData((prev) => ({
                      ...prev,
                      role_name: event.target.value,
                    }));
                    if (errors.role_name) {
                      setErrors((prev) => ({ ...prev, role_name: "" }));
                    }
                  }}
                  disabled={isLoading}
                />
              </TextField>

              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-body font-body text-default-font">
                  Description
                </span>
                <textarea
                  className="w-full min-h-[80px] rounded-lg border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font outline-none placeholder:text-neutral-400 focus:border-brand-600"
                  placeholder="Enter role description (optional)"
                  value={formData.description}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setFormData((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }));
                  }}
                  disabled={isLoading}
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.is_manager_required}
                  onCheckedChange={(checked) => {
                    setFormData((prev) => ({
                      ...prev,
                      is_manager_required: checked,
                    }));
                    // Clear review policies if manager is not required
                    if (!checked) {
                      setReviewPolicies({});
                    }
                  }}
                  label="Manager Required"
                />
                <span className="text-sm text-subtext-color">
                  If enabled, actions can be marked as requiring review
                </span>
              </div>
            </div>
          </div>

          {/* Permissions */}
          {loadingModules ? (
            <div className="flex items-center justify-center p-8">
              <span className="text-body font-body text-subtext-color">
                Loading permissions...
              </span>
            </div>
          ) : (
            <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Permissions
                </span>
                <span className="text-body font-body text-subtext-color">
                  Select the permissions for this role. If "Manager Required" is
                  enabled, you can also specify which actions require review.
                </span>
              </div>

              <RolePermissionMatrix
                modules={modules}
                permissions={permissions}
                reviewPolicies={reviewPolicies}
                isManagerRequired={formData.is_manager_required}
                onPermissionChange={handlePermissionChange}
                onReviewPolicyChange={handleReviewPolicyChange}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="neutral-secondary"
              onClick={() => navigate("/admin/dashboard?tab=roles")}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              type="submit"
              icon={<FeatherSave />}
              loading={isLoading}
              disabled={isLoading}
            >
              Create Role
            </Button>
          </div>
        </form>
      </div>
    </DefaultPageLayout>
  );
};
